'use client'

import ProductCard, { CARD_ASPECT } from "../product/ProductCard"
import { ArrowUpDown, Loader2, PackageOpen, SlidersHorizontal } from "lucide-react"
import { useCallback, useMemo, useRef, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { getProductTags, hasLoyaltyTierBenefits, isLoyaltyActive } from "@/utils/util"
import { SORT_OPTIONS, SPECIAL_CATEGORY_LABELS, SPECIAL_CATEGORY_FILTERS, findCategoryById, getCategoryDescendantIds, buildInitialProductFilters, type SpecialCategoryFilter } from "@/utils/product"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "../elements/EmptyState"
import SidePanel, { panelFocusRing } from "../elements/SidePanel"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useCart } from "@/lib/commerce/hooks"
import { Session } from "@/utils/types"

const secondNav = [
  { id: 'featured', name: 'Онцлох' },
  { id: 'new', name: 'Шинэ' },
  { id: 'sale', name: 'Хямдралтай' },
  { id: 'preorder', name: 'Захиалгат' },
  { id: 'loyalty', name: 'Лоялти' },
]

const gridClass = 'grid grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-8 lg:gap-y-12'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const sweep = 'pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] transition-[background-size] duration-500 ease-out motion-reduce:transition-none'
const sweepLine = (active: boolean) => `${sweep} ${active ? 'bg-size-[100%_1px]' : 'bg-size-[0%_1px] hover:bg-size-[100%_1px]'}`

const findCategoryPath = (list: any[], id: string): any[] => {
  for (const category of list || []) {
    if (category.id === id || category.slug === id) return [category]
    const sub = findCategoryPath(category.children || [], id)
    if (sub.length > 0) return [category, ...sub]
  }
  return []
}

export default function ProductList({ vendor, categoryList, brandList, session }: { vendor: any, categoryList: any, brandList: any, wishlist?: any, session?: Session | null }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const categories = categoryList || []
  const brands = brandList || []
  const [tags, setTags] = useState<string[]>([])
  const [localFilters, setLocalFilters] = useState<{ type: string, value: string }[]>([])
  const [sort, setSort] = useState('latest')
  const [filtersOpen, setFiltersOpen] = useState(false)

  const getInitialFilters = useCallback(() => buildInitialProductFilters(searchParams, categoryList), [searchParams, categoryList])

  const { loyaltySummary } = useCart()
  const navItems = useMemo(() => secondNav.filter((item) => item.id !== 'loyalty' || (isLoyaltyActive(vendor) && hasLoyaltyTierBenefits(loyaltySummary))), [vendor, loyaltySummary])

  const { products, isLoading, isLoadingMore, metadata, loadMore, updateFilters, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: getInitialFilters(),
    session
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  const [prevProducts, setPrevProducts] = useState(products)
  if (products !== prevProducts) {
    setPrevProducts(products)
    if (products && products.length > 0) {
      const allTags = products.flatMap((p: any) => getProductTags(p))
      setTags([...new Set(allTags)].filter(t => t !== '').sort((a, b) => a.localeCompare(b)))
    }
  }

  const paramsKey = searchParams.toString()
  const [prevParamsKey, setPrevParamsKey] = useState<string | null>(null)
  if (paramsKey !== prevParamsKey) {
    setPrevParamsKey(paramsKey)
    const fromParams: { type: string, value: string }[] = [
      ...(searchParams.get('brand')?.split(',').filter(Boolean) || []).map(value => ({ type: 'brand', value })),
      ...(searchParams.get('tag')?.split(',').filter(Boolean) || []).map(value => ({ type: 'tags', value })),
    ]
    if (fromParams.length > 0) setLocalFilters(fromParams)
  }

  const filterList = useMemo(() => ([
    ...(vendor?.modules?.includes('brand') ? [{ id: 'brand', name: 'Брэнд', options: (brands || []).map((b: any) => ({ value: b.id, label: b.name })) }] : []),
    { id: 'tags', name: 'Шошго', options: tags.map(t => ({ value: t, label: t })) },
  ]), [brands, tags, vendor])

  const activeCategoryParam = searchParams.get('category')
  const categoryPath = useMemo(() => activeCategoryParam ? findCategoryPath(categories, activeCategoryParam) : [], [categories, activeCategoryParam])

  const currentCategory = useMemo(() => {
    const brandParam = searchParams.get('brand')
    const tagParam = searchParams.get('tag')

    if (brandParam || tagParam) {
      const selectedItems: string[] = []
      if (brandParam) {
        const brandIds = brandParam.split(',')
        const selectedBrands = (brands || []).filter((brand: any) => brandIds.includes(brand.id))
        if (selectedBrands.length === 1) selectedItems.push(selectedBrands[0].name)
        else if (selectedBrands.length > 1) selectedItems.push(`${selectedBrands.length} брэнд`)
        else selectedItems.push('Брэнд')
      }
      if (tagParam) {
        const selectedTags = tagParam.split(',')
        selectedItems.push(selectedTags.length === 1 ? selectedTags[0] : `${selectedTags.length} шошго`)
      }
      if (selectedItems.length > 0) return { name: selectedItems.join(' · '), id: 'filtered' }
    }

    if (!activeCategoryParam) return null
    if (SPECIAL_CATEGORY_FILTERS.includes(activeCategoryParam as SpecialCategoryFilter)) {
      return { name: SPECIAL_CATEGORY_LABELS[activeCategoryParam as SpecialCategoryFilter], id: activeCategoryParam }
    }
    return findCategoryById(categoryList, activeCategoryParam)
  }, [searchParams, categoryList, brands, activeCategoryParam])

  const isActiveCategory = (category: any) => !!activeCategoryParam && (activeCategoryParam === category.slug || activeCategoryParam === category.id)

  const sortHandler = (value: string) => {
    setSort(value)
    updateFilters({ sort: value })
  }

  const updateFiltersInURL = (currentFilters: { type: string, value: string }[]) => {
    const params = new URLSearchParams(searchParams.toString())
    const brandFilters = currentFilters.filter(f => f.type === 'brand').map(f => f.value)
    const tagFilters = currentFilters.filter(f => f.type === 'tags').map(f => f.value)

    if (brandFilters.length > 0) params.set('brand', brandFilters.join(','))
    else params.delete('brand')
    if (tagFilters.length > 0) params.set('tag', tagFilters.join(','))
    else params.delete('tag')

    updateFilters({
      brand: brandFilters.length > 0 ? brandFilters.join(',') : null,
      tag: tagFilters.length > 0 ? tagFilters.join(',') : null
    })

    router.push(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const toggleFilter = (type: string, value: string, checked: boolean) => {
    const next = checked ? [...localFilters, { type, value }] : localFilters.filter(f => !(f.type === type && f.value === value))
    setLocalFilters(next)
    updateFiltersInURL(next)
  }

  const clearFilters = () => {
    setLocalFilters([])
    updateFiltersInURL([])
  }

  const isChecked = (type: string, value: string) => localFilters.some(f => f.type === type && f.value === value)

  const createQueryString = useCallback((name: string, value: any) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set(name, value)
    return params.toString()
  }, [searchParams])

  const setCategoryParams = (id: any) => {
    if (id == null) {
      updateFilters({ category: null, filter: null })
      return router.push(pathname)
    }

    if (SPECIAL_CATEGORY_FILTERS.includes(id as SpecialCategoryFilter)) {
      updateFilters({ category: null, filter: id })
    } else {
      const foundCategory = findCategoryById(categoryList, id)
      const categoryIds = foundCategory ? getCategoryDescendantIds(foundCategory) : [id]
      updateFilters({ category: categoryIds.join(','), filter: null })
    }

    router.push(pathname + '?' + createQueryString('category', id))
  }

  const selectCategory = (id: any) => {
    setCategoryParams(id)
    setFiltersOpen(false)
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const currentSortLabel = SORT_OPTIONS.find((o: any) => o.value === sort)?.label
  const activeFilterCount = localFilters.length

  const categoryRow = (category: any, depth: number) => {
    const active = isActiveCategory(category)
    const expanded = categoryPath.some((c: any) => c.id === category.id)
    return (
      <li key={category.id}>
        <button type="button" onClick={() => selectCategory(category.slug || category.id)} className={`block py-1.5 text-left text-[13px] transition-colors duration-150 ${depth > 0 ? 'text-muted-foreground hover:text-foreground' : ''} ${active ? 'text-foreground' : ''} ${panelFocusRing}`}>
          <span className={sweepLine(active)}>{category.name}</span>
        </button>
        {expanded && category.children?.length > 0 && (
          <ul className="ml-4 border-l border-border pl-4">
            {category.children.map((child: any) => categoryRow(child, depth + 1))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-16">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
        <button type="button" onClick={() => setCategoryParams(null)} className={`transition-colors duration-150 hover:text-foreground ${panelFocusRing}`}>Бүтээгдэхүүн</button>
        <span aria-hidden>/</span>
        {categoryPath.slice(0, -1).map((c: any) => (
          <span key={c.id} className="inline-flex items-center gap-x-1.5">
            <button type="button" onClick={() => setCategoryParams(c.slug || c.id)} className={`transition-colors duration-150 hover:text-foreground ${panelFocusRing}`}>{c.name}</button>
            <span aria-hidden>/</span>
          </span>
        ))}
      </nav>
      <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight text-foreground">{currentCategory ? currentCategory.name : 'Бүх бүтээгдэхүүн'}</h1>

      <div className="mt-8 lg:mt-10 mb-6 flex items-center justify-between gap-4">
        <div className="text-[13px] text-muted-foreground tabular-nums">
          {isLoading ? <Skeleton className="h-4 w-28 rounded-none" /> : `${metadata.total} бүтээгдэхүүн`}
        </div>

        <div className="flex items-center gap-6 sm:gap-8 text-[13px]">
          <div className="flex items-center gap-6 sm:gap-8">
            <button type="button" onClick={() => setFiltersOpen(true)} className={`inline-flex items-center gap-2 transition-opacity duration-150 hover:opacity-60 ${panelFocusRing}`}>
              Шүүх{activeFilterCount > 0 && <span className="tabular-nums">({activeFilterCount})</span>}
              <SlidersHorizontal className="size-4" strokeWidth={1.25} />
            </button>
            <Menu as="div" className="relative">
              <MenuButton className={`inline-flex items-center gap-2 transition-opacity duration-150 hover:opacity-60 ${panelFocusRing}`}>
                Эрэмбэ:
                <span className="hidden sm:inline text-muted-foreground">{currentSortLabel}</span>
                <ArrowUpDown className="size-4" strokeWidth={1.25} />
              </MenuButton>
              <MenuItems anchor="bottom end" className="z-30 w-52 border border-border bg-background py-2 [--anchor-gap:12px] focus:outline-none">
                {SORT_OPTIONS.map((option: any) => (
                  <MenuItem key={option.value}>
                    <button type="button" onClick={() => sortHandler(option.value)} className={`block w-full px-4 py-2 text-left text-[13px] data-focus:bg-foreground/5 ${sort === option.value ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {option.label}
                    </button>
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className={gridClass}>
          {Array.from({ length: 8 }).map((_, i) => <SkeletonProduct key={i} />)}
        </div>
      ) : products.length === 0 ? (
        <ProductNotFound />
      ) : (
        <>
          <div className={gridClass}>
            {products.map((product: any) => (
              <ProductCard key={product.id} product={product} vendor={vendor} />
            ))}
            {Array.from({ length: pendingCount }).map((_, i) => (
              <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                <SkeletonProduct animate={false} />
              </div>
            ))}
          </div>
          <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
            {isLoadingMore && (
              <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                <Loader2 className="size-4 animate-spin" strokeWidth={1.5} />
                <span>Ачаалж байна...</span>
              </div>
            )}
            {!hasMore && products.length > 0 && (
              <div className="flex flex-col items-center gap-4 text-[13px]">
                <span className="text-muted-foreground tabular-nums">Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})</span>
                <button type="button" onClick={scrollToTop} className={`h-10 px-6 border border-foreground tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${panelFocusRing}`}>Дээш буцах</button>
              </div>
            )}
          </div>
        </>
      )}

      <SidePanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Шүүх"
        footer={
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={clearFilters} disabled={activeFilterCount === 0} className={`h-12 border border-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground ${panelFocusRing}`}>Цэвэрлэх</button>
            <button type="button" onClick={() => setFiltersOpen(false)} className={`h-12 bg-foreground text-background text-[13px] tracking-wide tabular-nums transition-opacity duration-150 hover:opacity-85 ${panelFocusRing}`}>
              {isLoading ? 'Үр дүн харах' : `Үр дүн харах (${metadata.total})`}
            </button>
          </div>
        }
      >
        <div className="space-y-10">
          <section>
            <h3 className={eyebrow}>Бүлэг</h3>
            <ul className="mt-3">
              {navItems.map(item => (
                <li key={item.id}>
                  <button type="button" onClick={() => selectCategory(item.id)} className={`block py-1.5 text-[13px] ${panelFocusRing}`}>
                    <span className={sweepLine(activeCategoryParam === item.id)}>{item.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h3 className={eyebrow}>Ангилал</h3>
            <ul className="mt-3">
              <li>
                <button type="button" onClick={() => selectCategory(null)} className={`block py-1.5 text-[13px] ${panelFocusRing}`}>
                  <span className={sweepLine(!activeCategoryParam)}>Бүгд</span>
                </button>
              </li>
              {categories.map((category: any) => categoryRow(category, 0))}
            </ul>
          </section>

          {filterList.filter(section => section.options.length > 0).map(section => (
            <section key={section.id}>
              <h3 className={eyebrow}>{section.name}</h3>
              <div className="mt-3 space-y-3">
                {section.options.map((option: any) => (
                  <label key={option.value} className="flex items-center gap-3 text-[13px] cursor-pointer">
                    <input type="checkbox" value={option.value} checked={isChecked(section.id, option.value)} onChange={e => toggleFilter(section.id, option.value, e.target.checked)} className="size-4 rounded-none border-foreground/40 text-foreground focus:ring-foreground focus:ring-offset-0" />
                    <span className="min-w-0">{option.label}</span>
                  </label>
                ))}
              </div>
            </section>
          ))}
        </div>
      </SidePanel>
    </div>
  )
}

export function ProductNotFound() {
  return <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн хоосон байна" description="Яг одоогоор ямар нэгэн бүтээгдэхүүн байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
}

export function SkeletonProduct({ animate = true }: { aspectRatioClass?: string, animate?: boolean }) {
  const still = animate ? '' : 'animate-none'
  return (
    <div>
      <div className={`${CARD_ASPECT} w-full relative overflow-hidden`}>
        <Skeleton className={`absolute inset-0 w-full h-full rounded-none ${still}`} />
      </div>
      <Skeleton className={`h-3.5 w-3/4 mt-3 rounded-none ${still}`} />
      <Skeleton className={`h-3.5 w-1/3 mt-2 rounded-none ${still}`} />
    </div>
  )
}
